import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'

import styles from './styles.css'
import { getSpecialsSymbolNormalizedString, getSpecialsSymbolsRegExp } from '../../../utils/utils'
import { useTranslation } from 'react-i18next'
import {
  InputPassword,
  Tooltip,
  CheckRequirement,
  CheckedOptional,
  CheckedRequirement,
  CheckedWarning,
  WarningActiveRequirement,
  WarningRequirement
} from '../../../../ui'

const MEDIUM = 30
const STRONG = 100
const PERCENTS = 100

/**
 * @param {{
 *  id: string,
 *  passwordRequirements?: {
 *    min: number,
 *    max: number,
 *    upperCase: boolean,
 *    lowerCase: boolean,
 *    digits: boolean,
 *    specials: boolean,
 *    specialsAllowed: string,
 *    pattern: string
 *  },
 *  onChange: {value: string, (event: React.ChangeEvent): void},
 *  onBlur: {(event: React.FocusEvent): void},
 *  valid: boolean,
 *  label: string
 * }} props
 */
export const NewPasswordField =
  ({
    id,
    passwordRequirements,
    onChange,
    onBlur,
    valid,
    label
  }) => {
    const { t } = useTranslation()
    const [value, setValue] = useState('')
    const [conditions, setConditions] = useState(null)
    const [showTooltip, setShowTooltip] = useState(false)
    const [conditionsCount, setConditionsCount] = useState(0)
    const [showValidation, setShowValidation] = useState(false)

    const availableCharactersNumbersSymbols = useMemo(
      () =>
        `A-Za-z${passwordRequirements?.digits ? '0-9' : ''}${getSpecialsSymbolNormalizedString(
          passwordRequirements?.specialsAllowed || ''
        )}`,
      [passwordRequirements]
    )

    const invalidCharactersNumbersSymbolsRegExp = useMemo(
      () => new RegExp(`[^${availableCharactersNumbersSymbols}]`, 'g'),
      [availableCharactersNumbersSymbols]
    )

    const minMaxRegExp = useMemo(() => {
      const minMaxRestriction = passwordRequirements
        ? `{${passwordRequirements.min},${passwordRequirements.max}}`
        : ''
      return new RegExp(`^[${availableCharactersNumbersSymbols}]${minMaxRestriction}$`)
    }, [availableCharactersNumbersSymbols, passwordRequirements])

    const specialsAllowedRegExp = useMemo(
      () => getSpecialsSymbolsRegExp(passwordRequirements?.specialsAllowed ?? ''),
      [passwordRequirements]
    )

    const isFieldOptional = useCallback(
      (item) => {
        if (!passwordRequirements) {
          return
        }
        if (item === 'specialsAllowed' && !passwordRequirements.specials) {
          return true
        }
        return false
      },
      [passwordRequirements]
    )

    const filterInvalidCharacters = useCallback(
      (text) => text.replaceAll(invalidCharactersNumbersSymbolsRegExp, ''),
      [invalidCharactersNumbersSymbolsRegExp]
    )

    const processConditions = useCallback(
      (conditions, value) => {
        const currentConditions = { ...conditions }
        const keysConditions = Object.keys(currentConditions)
        keysConditions.forEach((key) => {
          if (currentConditions[key] != null) {
            switch (key) {
              case 'minMax':
                currentConditions[key] = minMaxRegExp.test(value)
                break
              case 'upperCase':
                currentConditions[key] = /[A-Z]/.test(value)
                break
              case 'lowerCase':
                currentConditions[key] = /[a-z]/.test(value)
                break
              case 'digits':
                currentConditions[key] = /[0-9]/.test(value)
                break
              case 'specialsAllowed':
                currentConditions[key] = specialsAllowedRegExp.test(value)
                break
            }
          }
        })
        const requirements = keysConditions.filter(
          (key) => currentConditions[key] != null && !isFieldOptional(key)
        )
        const completedRequirements = requirements.filter((key) =>
          Boolean(currentConditions[key])
        )
        setConditionsCount((completedRequirements.length / requirements.length) * PERCENTS || 0)
        return currentConditions
      }, [minMaxRegExp, specialsAllowedRegExp, isFieldOptional])

    const onChangeFn = useCallback(
      (event) => {
        const value = filterInvalidCharacters(event.target.value)
        setShowValidation(false)
        onChange(value, event)
        setValue(value)
        if (!passwordRequirements) {
          return
        }
        setConditions((prev) => {
          if (!prev) {
            return prev
          }
          return processConditions(prev, value)
        })
      },
      [passwordRequirements, onChange, filterInvalidCharacters, processConditions]
    )

    const onFocusFn = useCallback(() => {
      passwordRequirements && setShowTooltip(true)
    }, [passwordRequirements])

    const onBlurFn = useCallback((event) => {
      onBlur(event)
      passwordRequirements && setShowTooltip(false)
    }, [passwordRequirements, onBlur])

    const onInvalid = useCallback(() => {
      setShowValidation(true)
    }, [])

    useEffect(() => {
      if (passwordRequirements) {
        setConditions(() => {
          const conditions = {
            minMax: passwordRequirements?.min && passwordRequirements?.max ? false : null,
            upperCase: passwordRequirements?.upperCase ? false : null,
            lowerCase: passwordRequirements?.lowerCase ? false : null,
            digits: passwordRequirements?.digits ? false : null,
            specialsAllowed: passwordRequirements?.specialsAllowed ? false : null
          }
          return processConditions(conditions, value)
        })
      }
    }, [passwordRequirements])

    return (
      <div className={styles.newPasswordField} data-tooltip-id={id}>
        <InputPassword
          className={styles.input}
          value={value}
          label={label}
          name={id}
          onChange={onChangeFn}
          onFocus={onFocusFn}
          onBlur={onBlurFn}
          minLength={passwordRequirements?.min}
          valid={valid}
          onInvalid={onInvalid}
          required
        >
          <div
            className={classNames(styles.indicator, {
              [styles.show]: conditionsCount || showTooltip,
              [styles.week]: conditionsCount > 0 && conditionsCount < MEDIUM,
              [styles.medium]: conditionsCount >= MEDIUM && conditionsCount < STRONG,
              [styles.strong]: conditionsCount === STRONG
            })}
            style={{
              '--conditions-percents': conditionsCount - PERCENTS
            }}
          />
        </InputPassword>
        <Tooltip
          className={styles.tooltip}
          id={id}
          place='bottom'
          isOpen={showTooltip}
          mobileShow
        >
          <div className={styles.tooltipContent}>
            <p
              className={classNames(styles.tooltipTitle, {
                [styles.error]: showValidation
              })}
            >
              {t(showValidation ? 'newPassword.passwordConditionsNotMet' : 'newPassword.passwordConditions')}
              :
            </p>
            <ul
              className={styles.requirementsList}
            >
              {conditions &&
                Object.keys(conditions).reduce((acc, key) => {
                  if (conditions[key] != null) {
                    acc.push(
                      <TooltipItem
                        key={key}
                        condition={key}
                        kind={isFieldOptional(key) ? 'optional' : 'check'}
                        completed={!!conditions[key]}
                        showValidation={showValidation}
                        min={passwordRequirements?.min}
                        max={passwordRequirements?.max}
                        specialsAllowed={passwordRequirements?.specialsAllowed}
                      />
                    )
                  }
                  return acc
                }, [])}
            </ul>
          </div>
        </Tooltip>
      </div>
    )
  }

/**
 * @param {{
 *  condition: string,
 *  completed: boolean,
 *  min: number,
 *  max: number,
 *  kind: 'check' | 'warning' | 'optional',
 *  specialsAllowed: string,
 *  showValidation: boolean
 * }} props
 */
const TooltipItem =
  ({
    condition,
    completed,
    min,
    max,
    kind = 'check',
    specialsAllowed,
    showValidation
  }) => {
    const { t } = useTranslation()
    const Icon = useMemo(() => {
      switch (kind) {
        case 'check':
          return completed
            ? CheckedRequirement
            : showValidation
              ? CheckedWarning
              : CheckRequirement
        case 'warning':
          return completed ? WarningActiveRequirement : WarningRequirement
        case 'optional':
          return completed
            ? CheckedRequirement
            : showValidation
              ? CheckedOptional
              : CheckRequirement
      }
    }, [kind, completed, showValidation])
    return (
      <li
        className={classNames(styles.requirementsItem, {
          [styles.requirementsItemCompleted]:
            (completed && kind === 'check') ||
            (!completed && showValidation && kind === 'optional'),
          [styles.warningActive]:
            (completed && kind === 'warning') || (!completed && showValidation && kind === 'check')
        })}
      >
        <Icon className={styles.checkIcon} />
        <span>
          {t(`newPassword.${condition}`, {
            min,
            max,
            specialsAllowed
          }
          )}
        </span>
      </li>
    )
  }
